import { useAsyncEffect } from '@/hooks';
import { Column, Form, Grid, Input, Radio, Row, Segment, TextArea } from '@/semantic-ui/components';
import { useStores } from '@/store';
import { Formik, FormikProps } from 'formik';
import { differenceWith, isEqual } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { HasID, ID, moment, OnboardingDocumentProperties } from 'sol-data';
import { array, boolean, mixed, number, object, string } from 'yup';
import Breadcrumbs from '../Breadcrumbs';
import Bulletin from '../Bulletin';
import ButtonBar from '../ButtonBar';
import { CreateOnboardingDocumentModal } from '../CreateOnboardingDocumentModal';
import DragDrop from '../DragDrop';
import { PositionGroup } from '../PositionGroup';
import style from './create-onboarding-document-template.less';

const { Field } = Form;

export const MAX_FILE_SIZE = 15000000;
const acceptedFileTypes = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

export const CreateOnboardingDocumentTemplate = observer(() => {
  const { documentId } = useParams<{ documentId: string }>();
  const { onboardingDocumentStore, userStore } = useStores();
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [completedEmployees, setCompletedEmployees] = useState(0);
  const [shouldUpdateFile, setShouldUpdateFile] = useState(false);

  const [shouldOpenFinishModal, setShouldOpenFinishModal] = useState(false);
  // Modal state kept for future implementation
  const [_, setShouldOpenPreviewModal] = useState(false);

  useAsyncEffect(async () => {
    if (documentId !== 'add' && documentId !== undefined) {
      const { totalCount, completedCount } = await onboardingDocumentStore.fetchEmployeeCounts(
        documentId,
      );

      setTotalEmployees(totalCount);
      setCompletedEmployees(completedCount);
    }
  }, []);

  useEffect(() => {
    onboardingDocumentStore.clearCurrent();

    const fetchData = async () => {
      if (documentId && documentId !== 'add') {
        await onboardingDocumentStore.fetch(documentId);
      }
    };

    fetchData();
  }, [documentId, onboardingDocumentStore]);

  const { errorMessage, isLoading, current: onboardingDocument } = onboardingDocumentStore;

  let initialValues: OnboardingDocumentProperties & { file: File | null } = {
    title: '',
    description: '',
    azureBlobName: '',
    positions: [],
    fileName: '',
    file: null,
    requireSignature: false,
  };

  const { fullName } = userStore.current || { fullName: '' };
  let createdBy = `${fullName} - ${moment().format('YYYY-MM-DD')}`;

  if (onboardingDocument) {
    initialValues = {
      ...initialValues,
      title: onboardingDocument.title,
      description: onboardingDocument.description,
      azureBlobName: onboardingDocument.azureBlobName,
      positions: onboardingDocument.positions.map(({ id }) => ({
        id,
      })),
      fileName: onboardingDocument.fileName,
      file: onboardingDocument.file ?? null,
      requireSignature: onboardingDocument.requireSignature,
    };
    createdBy = `${onboardingDocument.postedBy} - ${onboardingDocument.postedOn}`;
  }

  const handleCreateDocument = async ({
    file,
    title,
    description,
    fileName,
    positions,
    requireSignature,
    setSubmitting,
  }: {
    file: File | null;
    title: string;
    description: string;
    fileName: string;
    positions: HasID[];
    requireSignature: boolean;
    setSubmitting: (isSubmitting: boolean) => void;
  }) => {
    const readyFormData = new FormData();

    if (file) {
      readyFormData.append('file', file);
    }

    const upload = await onboardingDocumentStore.uploadOnboardingDocumentPDF(readyFormData);
    await onboardingDocumentStore.create({
      title,
      description,
      azureBlobName: upload.azureBlobName,
      fileName,
      positions,
      requireSignature,
    });

    setSubmitting(false);

    if (onboardingDocumentStore.errorMessage === undefined) {
      setShouldOpenFinishModal(true);
    }

    return;
  };

  const handleUpdateDocument = async ({
    documentId,
    title,
    description,
    positions,
    requireSignature,
  }: {
    documentId: ID;
    file: File | null;
    title: string;
    description: string;
    fileName: string;
    positions: HasID[];
    requireSignature: boolean;
  }) => {
    // Just update normally
    await onboardingDocumentStore.update({
      id: documentId,
      positions,
      description,
      title,
      requireSignature,
    });
  };

  const handleUpdateWithNewFile = async ({
    documentId,
    file,
    title,
    description,
    positions,
    requireSignature,
  }: {
    documentId: ID;
    file: File;
    title: string;
    description: string;
    positions: HasID[];
    requireSignature: boolean;
  }) => {
    console.log(
      'handleUpdateWithNewFile',
      documentId,
      file,
      title,
      description,
      positions,
      requireSignature,
    );
    await onboardingDocumentStore.deleteFile(documentId);
    console.log('after delete file');

    const formData = new FormData();
    formData.append('file', file);

    const { fileName, azureBlobName } = await onboardingDocumentStore.uploadOnboardingDocumentPDF(
      formData,
    );

    console.log('after upload file', { fileName, azureBlobName });

    await onboardingDocumentStore.update({
      id: documentId,
      positions,
      description,
      title,
      requireSignature,
      azureBlobName,
      fileName,
    });

    console.log('after update document');

    setShouldUpdateFile(false);
  };

  const handlePostSubmit = (setSubmitting: (isSubmitting: boolean) => void) => {
    setSubmitting(false);

    if (onboardingDocumentStore.errorMessage === undefined) {
      setShouldOpenFinishModal(true);
    }
  };

  const closeModal = () => {
    setShouldOpenFinishModal(false);
  };

  return (
    <>
      <Segment basic padded="very">
        <Breadcrumbs
          linkTitle="Onboarding Documents"
          activeBreadcrumb={onboardingDocument?.title ?? 'New Form'}
          link="/onboarding/documents/templates"
          title="Onboarding Documents"
        />
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={object().shape({
            title: string().required('Title is required'),
            description: string().required('Description is required'),
            file: mixed()
              .required('File is required')
              .test(
                'fileSize',
                'File size must be less than 15mb',
                (file?: File | null | undefined) => {
                  if (!file) return documentId !== 'add'; // Allow null/undefined for existing documents
                  return file.size <= MAX_FILE_SIZE;
                },
              )
              .test(
                'fileType',
                `Unsupported file format. Please select ${Object.values(acceptedFileTypes)
                  .flat()
                  .join(', ')}.`,
                (file?: File | null | undefined) => {
                  if (!file) return documentId !== 'add'; // Allow null/undefined for existing documents
                  return Object.values(acceptedFileTypes)
                    .flat()
                    .includes(`.${file.name.split('.').pop()}`);
                },
              ),
            fileName: string().required('File name is required').label('File Name'),
            requireSignature: boolean().required('Signature is required'),
            positions: array()
              .of(
                object().shape({
                  id: number().min(1).required('Position must be selected'),
                }),
              )
              .min(1, 'You must select at least one position'),
          })}
          onSubmit={async (
            { file, title, description, fileName, positions, requireSignature },
            { setSubmitting },
          ) => {
            if (!documentId) {
              onboardingDocumentStore.setError('Document ID is required');
              return;
            }

            if (documentId === 'add') {
              await handleCreateDocument({
                file,
                title,
                description,
                fileName,
                positions,
                requireSignature,
                setSubmitting,
              });

              return handlePostSubmit(setSubmitting);
            }

            if (positions.length < initialValues.positions.length) {
              onboardingDocumentStore.setError('You cannot remove positions');
              return;
            }

            const modifiedPositions = differenceWith(positions, initialValues.positions, isEqual);

            // User had to delete file because of errors, so upload a new one and update
            // the document with the new file information
            if (shouldUpdateFile && file) {
              await handleUpdateWithNewFile({
                documentId,
                file,
                title,
                description,
                positions: modifiedPositions,
                requireSignature,
              });
            } else {
              await handleUpdateDocument({
                documentId,
                file,
                title,
                description,
                fileName,
                positions: modifiedPositions,
                requireSignature,
              });
            }

            return handlePostSubmit(setSubmitting);
          }}
        >
          {({
            handleSubmit,
            values,
            isSubmitting,
            handleChange,
            setFieldValue,
            touched,
            errors,
          }: FormikProps<OnboardingDocumentProperties & { file: File | null }>) => {
            const showTitleError = !!touched.title && !!errors.title;
            const showDescriptionError = !!touched.description && !!errors.description;
            const showPositionsError = !!touched.positions && !!errors.positions;
            const showFileError = !!touched.file && !!errors.file;
            const removeFile =
              documentId === 'add'
                ? () => {
                    setFieldValue('file', null);
                    setFieldValue('fileName', '');
                  }
                : undefined;

            return (
              <Grid>
                <div
                  style={{
                    color: completedEmployees === totalEmployees ? 'green' : 'red',
                  }}
                >
                  {totalEmployees > 0 && `${completedEmployees}/${totalEmployees} completed`}
                </div>
                <Row>
                  <Bulletin
                    errorMessage={errorMessage}
                    handleDismiss={() => onboardingDocumentStore.clearError()}
                    isLoading={isLoading}
                  />
                </Row>
                <Row>
                  <Column width={8}>
                    <Field>
                      <p className={style.form_headers}>Title</p>
                      <Input
                        name="title"
                        value={values.title}
                        placeholder="Title"
                        onChange={handleChange}
                        fluid
                      />
                      {showTitleError && <p className="error_class">{errors.title}</p>}
                    </Field>
                  </Column>
                  <Column width={8}>
                    <Field>
                      <p className={style.form_headers}>Created By</p>
                      <Input value={createdBy} placeholder={createdBy} readOnly fluid />
                    </Field>
                  </Column>
                </Row>
                <Row>
                  <Column width={8}>
                    <Field>
                      <p className={style.form_headers}>Description</p>
                      <Form>
                        <TextArea
                          name="description"
                          value={values.description}
                          placeholder="Details of document"
                          rows={15}
                          onChange={handleChange}
                          autoHeight
                          className={style.input}
                        />
                      </Form>
                      {showDescriptionError && <p className="error_class">{errors.description}</p>}
                    </Field>
                  </Column>
                  <Column width={8}>
                    <Field>
                      <p className={style.form_headers}>Upload Document</p>

                      <DragDrop
                        text="Drag and drop files"
                        dragText="Drop your file here!"
                        accept={acceptedFileTypes}
                        files={values.file && values.file instanceof File ? [values.file] : []}
                        allowReplace={documentId === 'add'}
                        height={285}
                        onFileDrop={(files: File[]) => {
                          if (files?.[0]) {
                            setFieldValue('fileName', files[0].name);
                            setFieldValue('file', files[0]);
                          }
                        }}
                        onFileDelete={(_, hasError) => {
                          if (hasError) {
                            setShouldUpdateFile(true);
                          }

                          setFieldValue('fileName', '');
                          setFieldValue('file', undefined);
                        }}
                      />
                      {showFileError && <p className="error_class">{String(errors.file)}</p>}
                    </Field>
                  </Column>
                </Row>
                <Row>
                  <Column width={16}>
                    <Radio
                      name="requireSignature"
                      label="Require Signature"
                      toggle
                      checked={values.requireSignature}
                      onChange={(_, { checked }) => setFieldValue('requireSignature', checked)}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column width={16}>
                    <p className={style.form_headers}>Distribution</p>
                    <p>Select positions below that will see this document during onboarding</p>
                    <PositionGroup
                      handlePositionClick={setFieldValue}
                      selectedPositions={values.positions}
                    />
                    {showPositionsError && (
                      <p className="error_class">{String(errors.positions)}</p>
                    )}
                  </Column>
                </Row>
                <Row>
                  <Column width={16}>
                    <ButtonBar
                      onSave={handleSubmit}
                      link="/onboarding/documents/templates"
                      disabled={isSubmitting}
                      saveText="Publish"
                    />
                  </Column>
                </Row>
              </Grid>
            );
          }}
        </Formik>
      </Segment>

      <CreateOnboardingDocumentModal open={shouldOpenFinishModal} handleClose={closeModal} />
    </>
  );
});
